'use client';

import { useState, useEffect } from 'react';
import { resolveAssetUrl } from '../lib/api-base';

export type LoadingSpinnerType = 'circle' | 'logo' | 'logoY' | 'logoX' | 'none';

interface Props {
  productName?: string;
  metadata?: Record<string, any>;
  progress?: number;
  visible: boolean;
  /** Softer copy while materials finish after models are in. */
  phase?: 'assets' | 'finishing';
}

function parseSpinnerType(value: unknown): LoadingSpinnerType {
  if (value === 'circle' || value === 'logo' || value === 'logoY' || value === 'logoX' || value === 'none') {
    return value;
  }
  return 'circle';
}

function isLogoSpinner(type: LoadingSpinnerType): boolean {
  return type === 'logo' || type === 'logoY' || type === 'logoX';
}

function logoSpinAnimation(type: LoadingSpinnerType): string {
  if (type === 'logoY') return 'c3d-load-spin-y';
  if (type === 'logoX') return 'c3d-load-spin-x';
  return 'c3d-load-spin'; // flat Z
}

export function LoadingScreen({ productName, metadata, progress = 0, visible, phase = 'assets' }: Props) {
  const [fadeOut, setFadeOut] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Allow first paint at opacity 0, then ease in the loader itself.
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!visible) {
      setFadeOut(true);
      const timer = setTimeout(() => setFadeOut(false), 720);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  if (!visible && !fadeOut) return null;

  const theme = (metadata as any)?.playerTheme || {};
  const loading = (metadata as any)?.loadingScreen || {};

  const bgColor = loading.backgroundColor || theme.backgroundColor || '#0c0b0a';
  const textColor = loading.textColor || theme.textColor || '#f5f0e8';
  const accentColor = loading.accentColor || theme.primaryColor || '#c4a574';
  const brandLogoUrl = resolveAssetUrl(loading.logoUrl || theme.logoUrl || '');
  const spinnerType = parseSpinnerType(loading.spinner ?? loading.spinnerType);
  const spinnerLogoUrl = resolveAssetUrl(
    loading.spinnerLogoUrl || loading.spinnerUrl || brandLogoUrl || theme.logoUrl || '',
  );
  const spinnerSize = Math.min(120, Math.max(28, Number(loading.spinnerSize) || 56));
  const spinnerOrigin = (loading.spinnerOrigin as string) || '50% 48%';
  const spinnerDuration = Math.min(4, Math.max(0.6, Number(loading.spinnerDuration) || 1.4));
  const tagline =
    loading.tagline ||
    (phase === 'finishing' ? 'Preparing your experience' : 'Loading experience');
  const showProgress = loading.showProgress !== false;
  const displayProgress = phase === 'finishing' ? Math.max(progress, 92) : progress;
  const logoSpin = isLogoSpinner(spinnerType);
  const needsPerspective = spinnerType === 'logoY' || spinnerType === 'logoX';

  // Avoid duplicating the same mark as both spinner and static brand logo.
  const showStaticBrand =
    !!brandLogoUrl &&
    (!logoSpin || brandLogoUrl !== spinnerLogoUrl);

  return (
    <div
      className={`fixed inset-0 z-[2000] flex flex-col items-center justify-center transition-opacity ease-out ${
        !visible || !mounted ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      style={{
        backgroundColor: bgColor,
        color: textColor,
        transitionDuration: visible ? '500ms' : '700ms',
      }}
      aria-busy={visible}
      aria-live="polite"
    >
      {/* Soft vignette: keeps the wait feeling composed, not like a progress dump */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 70% 55% at 50% 42%, ${accentColor}14 0%, transparent 62%)`,
        }}
      />

      <div className="relative flex flex-col items-center px-6">
        {logoSpin && spinnerLogoUrl ? (
          <div
            className="mb-8 flex items-center justify-center"
            style={{
              width: spinnerSize,
              height: spinnerSize,
              perspective: needsPerspective ? Math.round(spinnerSize * 3.2) : undefined,
            }}
          >
            <img
              src={spinnerLogoUrl}
              alt=""
              className="max-h-full max-w-full object-contain opacity-95"
              style={{
                transformOrigin: spinnerOrigin,
                transformStyle: needsPerspective ? 'preserve-3d' : undefined,
                animation: `${logoSpinAnimation(spinnerType)} ${spinnerDuration}s linear infinite`,
                filter: loading.spinnerInvert ? 'invert(1)' : undefined,
                backfaceVisibility: needsPerspective ? 'visible' : undefined,
              }}
            />
          </div>
        ) : spinnerType === 'circle' ? (
          <div className="mb-8 flex h-12 w-12 items-center justify-center">
            <span
              className="block h-8 w-8 rounded-full border"
              style={{
                borderColor: `${accentColor}55`,
                borderTopColor: accentColor,
                animation: 'c3d-load-spin 1.1s linear infinite',
              }}
            />
          </div>
        ) : null}

        {showStaticBrand && (
          <img
            src={brandLogoUrl}
            alt=""
            className="mb-6 h-10 max-w-[200px] object-contain opacity-95"
          />
        )}

        {productName && (
          <h1
            className="mb-2 max-w-md text-center text-[15px] font-medium tracking-[0.18em] uppercase opacity-90"
            style={{ color: textColor }}
          >
            {productName}
          </h1>
        )}

        <p className="mb-10 text-center text-[12px] font-light tracking-[0.12em] opacity-45" style={{ color: textColor }}>
          {tagline}
        </p>

        {showProgress && (
          <div className="w-40 space-y-2">
            <div className="h-px overflow-hidden rounded-full" style={{ backgroundColor: `${textColor}18` }}>
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${Math.min(100, Math.max(8, displayProgress))}%`,
                  backgroundColor: accentColor,
                  opacity: 0.85,
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-5 text-[9px] tracking-[0.2em] uppercase opacity-25" style={{ color: textColor }}>
        Threekit
      </div>

      <style>{`
        @keyframes c3d-load-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes c3d-load-spin-y {
          to { transform: rotateY(360deg); }
        }
        @keyframes c3d-load-spin-x {
          to { transform: rotateX(360deg); }
        }
      `}</style>
    </div>
  );
}
